/**
 * Data Source Configuration Page
 *
 * Main page for managing data source connections, credentials,
 * and synchronization settings.
 */

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  CheckCircle as HealthyIcon,
  PlayArrow as SyncIcon,
  Help as UnknownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { DataSourceForm } from '../components/configuration/DataSourceForm';
import { SyncJobMonitor } from '../components/configuration/SyncJobMonitor';
import { useDataSourceConfiguration } from '../hooks/useDataSourceConfiguration';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`data-source-tabpanel-${index}`}
      aria-labelledby={`data-source-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const DataSourceConfigurationPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedDataSource, setSelectedDataSource] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [dataSourceToDelete, setDataSourceToDelete] = useState<any>(null);

  const {
    dataSources,
    loading,
    error,
    createDataSource,
    updateDataSource,
    deleteDataSource,
    testConnection,
    startSync,
    refreshDataSources,
  } = useDataSourceConfiguration();

  useEffect(() => {
    refreshDataSources();
  }, [refreshDataSources]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddDataSource = () => {
    setSelectedDataSource(null);
    setIsFormOpen(true);
  };
  const handleEditDataSource = (dataSource: any) => {
    setSelectedDataSource(dataSource);
    setIsFormOpen(true);
  };

  const handleDeleteDataSource = (dataSource: any) => {
    setDataSourceToDelete(dataSource);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (dataSourceToDelete) {
      await deleteDataSource(dataSourceToDelete.id);
      setDeleteConfirmOpen(false);
      setDataSourceToDelete(null);
    }
  };

  const handleFormSubmit = async (formData: any) => {
    if (selectedDataSource) {
      await updateDataSource(selectedDataSource.id, formData);
    } else {
      await createDataSource(formData);
    }
    setIsFormOpen(false);
    setSelectedDataSource(null);
  };

  const handleTestConnection = async (dataSourceId: string) => {
    await testConnection(dataSourceId);
  };

  const handleStartSync = async (
    dataSourceId: string,
    type: 'full' | 'incremental'
  ) => {
    await startSync(dataSourceId, type);
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <HealthyIcon color='success' />;
      case 'warning':
        return <WarningIcon color='warning' />;
      case 'error':
        return <ErrorIcon color='error' />;
      default:
        return <UnknownIcon color='disabled' />;
    }
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h4' component='h1'>
          Data Source Configuration
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleAddDataSource}
        >
          Add Data Source
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label='Data Sources' />
          <Tab label='Sync Jobs' />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {dataSources.map(dataSource => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={dataSource.id}>
              <Card>
                <CardContent>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='flex-start'
                    mb={2}
                  >
                    <Box>
                      <Typography variant='h6' component='h3'>
                        {dataSource.name}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {dataSource.type.replace('-', ' ').toUpperCase()}
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center' gap={1}>
                      <Chip
                        icon={getHealthIcon(dataSource.healthStatus.status)}
                        label={dataSource.healthStatus.status}
                        color={
                          getHealthColor(dataSource.healthStatus.status) as any
                        }
                        size='small'
                      />
                      <Chip
                        label={dataSource.enabled ? 'Enabled' : 'Disabled'}
                        color={dataSource.enabled ? 'success' : 'default'}
                        size='small'
                      />
                    </Box>
                  </Box>

                  <Typography variant='body2' color='text.secondary' mb={2}>
                    Last sync:{' '}
                    {dataSource.lastSyncAt
                      ? new Date(dataSource.lastSyncAt).toLocaleString()
                      : 'Never'}
                  </Typography>

                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Box>
                      <IconButton
                        size='small'
                        onClick={() => handleEditDataSource(dataSource)}
                        title='Edit'
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => handleDeleteDataSource(dataSource)}
                        title='Delete'
                        color='error'
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box>
                      <Button
                        size='small'
                        onClick={() => handleTestConnection(dataSource.id)}
                        disabled={!dataSource.enabled}
                      >
                        Test
                      </Button>
                      <Button
                        size='small'
                        startIcon={<SyncIcon />}
                        onClick={() =>
                          handleStartSync(dataSource.id, 'incremental')
                        }
                        disabled={!dataSource.enabled}
                        sx={{ ml: 1 }}
                      >
                        Sync
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {dataSources.length === 0 && (
          <Box textAlign='center' py={8}>
            <Typography variant='h6' color='text.secondary' mb={2}>
              No data sources configured
            </Typography>
            <Typography variant='body2' color='text.secondary' mb={3}>
              Add your first data source to start collecting metrics
            </Typography>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAddDataSource}
            >
              Add Data Source
            </Button>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <SyncJobMonitor dataSources={dataSources} />
      </TabPanel>

      {/* Data Source Form Dialog */}
      <Dialog
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selectedDataSource ? 'Edit Data Source' : 'Add Data Source'}
        </DialogTitle>
        <DialogContent>
          <DataSourceForm
            dataSource={selectedDataSource}
            onSubmit={handleFormSubmit}
            onCancel={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the data source "
            {dataSourceToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color='error' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSourceConfigurationPage;
